import { useMemo, useRef, useState, useEffect } from "react";
import { uploadDepartures, sendDepartureReport } from "../utils/api";
import { IGLU_ORANGE } from "../data/map";



const TEMPLATE_URL = "/static/pdffilters/requests.xlsx";
const ACCENT = IGLU_ORANGE;                      // #F05223
const ACCENT_RGB = "240,82,35";

const toneColor = (t) =>
  t === "red" ? "#f87171" : t === "green" ? "#4ade80" : t === "amber" ? "#fbbf24" : "#94a3b8";

const S = {
  // topOffset is the backdrop's top padding. The backdrop itself starts UNDER the info bar
  // (--fr-modal-top), so the default is the same 40px breathing room FreshopsUpload uses.
  backdrop: (topOffset) => ({ position:"fixed", inset:0, top: "var(--fr-modal-top, 0px)", background:"rgba(2,6,23,0.7)", zIndex:1100,
    display:"flex", alignItems:"flex-start", justifyContent:"center",
    padding:`${topOffset} 16px 40px`, overflowY:"auto" }),
  modal: { position:"relative", maxHeight: "calc(var(--fr-modal-max, 82vh) - 76px)", overflowY: "auto", overscrollBehavior: "contain", width:"100%", maxWidth:960,
    background:"linear-gradient(135deg,#1e293b 0%,#0f172a 100%)",
    border:`1px solid rgba(${ACCENT_RGB},0.3)`, borderRadius:16, padding:"18px 18px 14px" },
  accent: { position:"absolute", top:0, left:0, right:0, height:3,
    background:`linear-gradient(90deg,${ACCENT},#fb923c,#fbbf24)` },
  head: { position: "sticky", top: 0, zIndex: 5, background: "#1e293b", boxShadow: "0 -18px 0 0 #1e293b",  display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14, gap:10 },
  titleWrap: { display:"flex", alignItems:"center", gap:10 },
  logo: { width:28, height:28, objectFit:"contain" },
  title: { margin:0, fontSize:"1rem", fontWeight:700, color:"#f1f5f9" },
  closeX: { background:"none", border:"none", color:"rgba(255,255,255,0.6)", fontSize:"1.4rem", lineHeight:1, cursor:"pointer" },
  drop: (over) => ({ border:`2px dashed ${over ? ACCENT : `rgba(${ACCENT_RGB},0.4)`}`,
    borderRadius:14, padding:"44px 20px", textAlign:"center", cursor:"pointer",
    background: over ? `rgba(${ACCENT_RGB},0.08)` : "rgba(255,255,255,0.02)", transition:"all .15s ease" }),
  dropTitle: { color:"#e2e8f0", fontWeight:700, fontSize:"0.95rem", display:"flex",
    alignItems:"center", justifyContent:"center", gap:10, flexWrap:"wrap" },
  removeX: { width:22, height:22, borderRadius:"50%", border:"1px solid rgba(248,113,113,0.5)",
    background:"rgba(248,113,113,0.12)", color:"#fca5a5", fontSize:"0.85rem", lineHeight:1,
    cursor:"pointer", display:"inline-flex", alignItems:"center", justifyContent:"center", padding:0 },
  emailRow: { display:"flex", alignItems:"center", gap:8, marginTop:10, fontSize:"0.8rem",
    color:"#e2e8f0", cursor:"pointer", userSelect:"none" },
  dropHint: { color:"rgba(255,255,255,0.45)", fontSize:"0.78rem", marginTop:6 },
  templateRow: { display:"flex", justifyContent:"space-between", alignItems:"center", gap:10, marginTop:10, flexWrap:"wrap" },
  templateBtn: { display:"inline-flex", alignItems:"center", gap:8, padding:"8px 14px", borderRadius:10,
    border:`1px solid rgba(${ACCENT_RGB},0.5)`, background:`rgba(${ACCENT_RGB},0.1)`, color:ACCENT,
    fontWeight:700, fontSize:"0.78rem", textDecoration:"none", cursor:"pointer" },
  buttonsGrid: { display:"grid", gridTemplateColumns:"repeat(3,minmax(0,1fr))", gap:10, margin:"12px 0 8px" },
  countBtn: (active) => ({ borderRadius:12, padding:"12px 10px", cursor:"pointer",
    background: active ? `rgba(${ACCENT_RGB},0.16)` : `rgba(${ACCENT_RGB},0.07)`,
    border:`1px solid ${active ? `rgba(${ACCENT_RGB},0.65)` : `rgba(${ACCENT_RGB},0.25)`}`,
    display:"flex", flexDirection:"column", alignItems:"center", gap:4 }),
  countLabel: { fontSize:"0.68rem", color:"rgba(255,255,255,0.55)", textTransform:"uppercase",
    letterSpacing:"0.05em", fontWeight:700 },
  countValue: (tone) => ({ fontSize:"1.7rem", fontWeight:800, lineHeight:1, color: toneColor(tone) }),
  card: { background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.08)",
    borderRadius:12, padding:"12px", marginTop:10 },
  colHead: { fontSize:"0.62rem", letterSpacing:"0.05em", textTransform:"uppercase",
    color:"rgba(255,255,255,0.45)", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" },
  cell: { fontSize:"0.8rem", color:"#e2e8f0", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" },
  room: { fontSize:"0.84rem", fontWeight:700, color:ACCENT, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" },
  rowNum: { fontSize:"0.7rem", color:"rgba(255,255,255,0.4)" },
  errText: { fontSize:"0.72rem", color:"#fca5a5", whiteSpace:"normal", lineHeight:1.4 },
  ynChip: (yes) => ({ fontSize:"0.6rem", fontWeight:700, padding:"1px 7px", borderRadius:999,
    background: yes ? "rgba(74,222,128,0.12)" : "rgba(255,255,255,0.05)",
    color: yes ? "#4ade80" : "rgba(255,255,255,0.45)",
    border: `1px solid ${yes ? "rgba(74,222,128,0.35)" : "rgba(255,255,255,0.12)"}` }),
  submit: (busy, ready) => ({ width:"100%", padding:"12px", borderRadius:10, border:"none", marginTop:12,
    background: ready ? `linear-gradient(135deg,${ACCENT} 0%,#fb923c 100%)` : "rgba(255,255,255,0.06)",
    color: ready ? "#0f172a" : "rgba(255,255,255,0.5)", fontWeight:800, fontSize:"0.9rem",
    cursor: busy ? "wait" : ready ? "pointer" : "not-allowed",
    display:"flex", alignItems:"center", justifyContent:"center", gap:8 }),
  done: { margin:"14px 0", padding:"14px", borderRadius:12, textAlign:"center", fontSize:"0.9rem",
    background:"rgba(74,222,128,0.08)", border:"1px solid rgba(74,222,128,0.35)", color:"#4ade80", fontWeight:700 },
  doneMsg: { marginTop:8, fontSize:"0.75rem", color:"#fca5a5", fontWeight:500, textAlign:"left" },
  hint: { marginTop:8, fontSize:"0.72rem", color:"rgba(255,255,255,0.4)", textAlign:"center" },
  err: { margin:"10px 0", padding:"9px 12px", borderRadius:10, fontSize:"0.8rem",
    background:"rgba(248,113,113,0.08)", border:"1px solid rgba(248,113,113,0.35)", color:"#fca5a5" },
  smallBtn: { padding:"6px 14px", borderRadius:8, fontSize:"0.78rem", fontWeight:700, cursor:"pointer",
    border:"1px solid rgba(74,222,128,0.5)", background:"rgba(74,222,128,0.12)", color:"#4ade80" },
  closeBtn: { width:"100%", padding:"11px", borderRadius:10, marginTop:12, cursor:"pointer",
    border:"1px solid rgba(255,255,255,0.12)", background:"rgba(255,255,255,0.06)", color:"#e2e8f0", fontWeight:600 },
  launcher: { position:"relative", overflow:"hidden", maxWidth:560, margin:"24px auto",
    background:"linear-gradient(135deg,#1e293b 0%,#0f172a 100%)",
    border:`1px solid rgba(${ACCENT_RGB},0.3)`, borderRadius:16, padding:"22px 20px 18px", textAlign:"center" },
  launcherLogo: { width:64, height:64, objectFit:"contain", display:"block", margin:"0 auto 10px" },
  launcherTitle: { margin:0, fontSize:"1rem", fontWeight:700, color:"#f1f5f9" },
  launcherText: { margin:"6px 0 14px", fontSize:"0.8rem", color:"rgba(255,255,255,0.5)", lineHeight:1.5 },
  launcherBtns: { display:"flex", gap:10, justifyContent:"center", flexWrap:"wrap" },
  openBtn: { padding:"10px 18px", borderRadius:10, border:"none", cursor:"pointer",
    background:`linear-gradient(135deg,${ACCENT} 0%,#fb923c 100%)`, color:"#0f172a", fontWeight:800, fontSize:"0.85rem" },
  toast: { position:"fixed", top:20, left:"50%", transform:"translateX(-50%)", zIndex:1200,
    display:"flex", alignItems:"center", gap:10, padding:"12px 18px", borderRadius:12,
    background:"rgba(22,163,74,0.95)", color:"#fff", fontWeight:700, fontSize:"0.88rem",
    boxShadow:"0 10px 30px rgba(0,0,0,0.4)", border:"1px solid rgba(74,222,128,0.6)" },
};

const Spinner = () => (
  <span style={{ width:12, height:12, border:"2px solid currentColor", borderTopColor:"transparent",
    borderRadius:"50%", display:"inline-block", animation:"du-spin 0.7s linear infinite" }} />
);

const BUCKETS = [
  { key:"total",   label:"Total Rows", tone:"slate" },
  { key:"valid",   label:"Ready",      tone:"green" },
  { key:"invalid", label:"Errors",     tone:"red"   },
];

const TABLE_COLS = "0.45fr 0.9fr 1fr 1.4fr 0.9fr 0.6fr 0.6fr 0.6fr 0.6fr 1.3fr";
const HEAD = ["Row", "Request", "Room", "Type", "Clean", "Bed", "Bath", "Kit", "Turn", "Email"];

const fmtDate = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return d && m && y ? `${d}/${m}/${y}` : iso;
};

const DepartureUpload = ({ onClose, topOffset = "40px", autoOpen = false }) => {
  // Controlled: parent toggles us and passes onClose (FreshopsUpload pattern).
  // Self-managed: mounted straight into a tab — we show a launcher when closed.
  const controlled = typeof onClose === "function";
  const [open, setOpen] = useState(controlled || autoOpen);
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [selection, setSelection] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [toast, setToast] = useState(null);
  const [emailReport, setEmailReport] = useState(true);
  const inputRef = useRef(null);

  // Push progress: bar advances while the request is in flight, count ticks per row
  const [phase, setPhase] = useState(null);          // 'push' | 'email' | null
  const [visual, setVisual] = useState(0);
  const barRef = useRef(null);

  const startBar = (cap) => {
    clearInterval(barRef.current);
    setVisual(0);
    barRef.current = setInterval(() => {
      setVisual((v) => (v >= cap ? v : Math.min(cap, v + Math.max(0.4, (cap - v) * 0.06))));
    }, 80);
  };
  const stopBar = (pct) => { clearInterval(barRef.current); setVisual(pct); };

  const [shown, setShown] = useState(0);             // rows counted so far (ticks while the push runs)
  const [stage, setStage] = useState("");
  const countRef = useRef(null);

  // The push is one request the server batches 10 at a time; tick the count
  // through the rows while it runs, narrating the batch it's on, then snap to created.
  const startCount = (total) => {
    clearInterval(countRef.current);
    let n = 0;
    setShown(0);
    setStage("Validating rows…");
    const step = Math.max(90, Math.min(350, 2500 / Math.max(1, total)));
    countRef.current = setInterval(() => {
      if (n < total - 1) {
        n += 1;
        setShown(n);
        const batch = Math.floor((n - 1) / 10) + 1;
        setStage(`Pushing row ${n} / ${total} (batch ${batch} of ${Math.ceil(total / 10)})…`);
      } else {
        setStage("Waiting for Airtable to confirm…");
      }
    }, step);
  };
  const stopCount = (n) => { clearInterval(countRef.current); setShown(n); };
  useEffect(() => () => { clearInterval(barRef.current); clearInterval(countRef.current); }, []);

  const close = () => {
    if (controlled) onClose();
    else setOpen(false);          // back to the launcher, still mounted in the tab
  };

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose, open]);

  const runPreview = async (f) => {
    setBusy(true); setError(""); setResult(null);
    const res = await uploadDepartures(f, false);
    setBusy(false);
    if (res.status === "success") { setPreview(res); setSelection(res.invalid > 0 ? "invalid" : null); }
    else setError(res.error || "Could not read that file");
  };

  const pickFile = (f) => {
    if (!f) return;
    if (!/\.xlsx?$|\.xlsm$/i.test(f.name)) { setError("Please drop the requests.xlsx template"); return; }
    setFile(f);
    runPreview(f);
  };

  const commit = async () => {
    if (!preview || preview.valid === 0 || busy) return;
    setBusy(true); setError("");
    setPhase("push");
    startBar(88);
    startCount(counts.valid);
    const res = await uploadDepartures(file, true);
    if (res.status === "success") {
      const parts = [`${res.created} of ${res.total} departure clean${res.total === 1 ? "" : "s"} created`];
      if (res.skipped) parts.push(`${res.skipped} skipped`);
      if (res.failed) parts.push(`${res.failed} failed`);
      stopCount(res.created || 0);
      setStage(`${res.created} of ${res.total} created${res.skipped ? `, ${res.skipped} skipped` : ""}${res.failed ? `, ${res.failed} failed` : ""}`);
      stopBar(emailReport && res.created > 0 ? 92 : 100);
      if (emailReport && res.created > 0) {
        setPhase("email");
        setStage("Building PDF & emailing recipients…");
        const pushed = (res.rows || []).filter((r) => !r.errors?.length);
        try {
          await sendDepartureReport(pushed, "upload");
          parts.push("PDF emailed");
          res.emailed = true;
        } catch (e) {
          parts.push("email failed");
          res.emailError = e.message;
        }
      }
      stopBar(100);
      setStage(`Done — ${res.created} created${res.emailed ? ", PDF emailed" : ""}`);
      setResult(res);
      setToast(parts.join(" · "));
      await new Promise((r) => setTimeout(r, 700));   // let 100% be seen
    } else {
      stopCount(0);
      stopBar(0);
      setStage("");
      setError(res.error || "Push failed");
      setToast(null);
    }
    setPhase(null);
    setBusy(false);
  };

  const reset = () => { setFile(null); setPreview(null); setResult(null); setSelection(null); setError(""); };

  const rows = preview?.rows || [];
  const validRows = useMemo(() => rows.filter((r) => !r.errors?.length), [rows]);
  const invalidRows = useMemo(() => rows.filter((r) => r.errors?.length), [rows]);
  const counts = { total: preview?.total ?? 0, valid: preview?.valid ?? 0, invalid: preview?.invalid ?? 0 };

  const rowLine = (r, i, showErr) => (
    <div key={r.rowNum} className="du-row"
      style={{ display:"grid", gridTemplateColumns:TABLE_COLS, alignItems:"center", padding:"9px 8px",
        columnGap:8, borderTop: i ? "1px solid rgba(255,255,255,0.06)" : "none" }}>
      <span style={S.rowNum}>#{r.rowNum}</span>
      <span style={S.cell}>{fmtDate(r.requestDate)}</span>
      <span style={S.room}>{r.room || "—"}</span>
      <span style={S.cell}>{r.roomType || "—"}</span>
      <span style={S.cell}>{fmtDate(r.cleanDate)}</span>
      <span><span style={S.ynChip(r.bedroom === "Yes")}>{r.bedroom || "—"}</span></span>
      <span><span style={S.ynChip(r.bathroom === "Yes")}>{r.bathroom || "—"}</span></span>
      <span><span style={S.ynChip(r.kitchen === "Yes")}>{r.kitchen || "—"}</span></span>
      <span><span style={S.ynChip(r.turnover === "Yes")}>{r.turnover || "—"}</span></span>
      <span style={S.cell}>{r.email || "—"}</span>
      {showErr && r.errors?.length > 0 && (
        <div style={{ gridColumn:"1 / -1", ...S.errText, paddingTop:4 }}>
          ⚠ {r.errors.join(" · ")}
        </div>
      )}
    </div>
  );

  const table = (list, showErr) => (
    <div style={S.card}>
      {list.length === 0 ? (
        <div style={{ ...S.hint, padding:"14px 0" }}>{showErr ? "No errors 🎉" : "Nothing here"}</div>
      ) : (
        <div style={{ maxHeight:320, overflowY:"auto", overflowX:"auto" }}>
          <div style={{ minWidth:820 }}>
            <div style={{ display:"grid", gridTemplateColumns:TABLE_COLS, padding:"8px 8px 10px", columnGap:8,
              borderBottom:"1px solid rgba(255,255,255,0.12)", position:"sticky", top:0, background:"#1b2537", zIndex:1 }}>
              {HEAD.map((h) => <span key={h} style={S.colHead}>{h}</span>)}
            </div>
            {list.map((r, i) => rowLine(r, i, showErr))}
          </div>
        </div>
      )}
    </div>
  );

  if (!open) {
    return (
      <div style={S.launcher}>
        <div style={S.accent} />
        {toast && <div style={{ ...S.toast, position:"fixed" }}>{toast}</div>}
        <img src="/static/images/iglu_logo_transparent.png" alt="IGLU South Yarra" style={S.launcherLogo} />
        <h4 style={S.launcherTitle}>Departure Clean Upload</h4>
        <p style={S.launcherText}>
          Drop the requests.xlsx template to create departure cleans in bulk.
        </p>
        <div style={S.launcherBtns}>
          <button type="button" style={S.openBtn} onClick={() => setOpen(true)}>⬆ Open Upload</button>
          <a href={TEMPLATE_URL} download="requests.xlsx" style={S.templateBtn}>Download Template</a>
        </div>
      </div>
    );
  }

  return (
    <div style={S.backdrop(topOffset)} onClick={busy ? undefined : close}>
      <style>{`@keyframes du-spin{to{transform:rotate(360deg)}}
        .du-row{transition:background .12s ease}.du-row:hover{background:rgba(${ACCENT_RGB},0.06)}`}</style>

      {toast && (
        <div style={S.toast} onClick={(e) => e.stopPropagation()}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
          {toast}
        </div>
      )}

      <div style={S.modal} onClick={(e) => e.stopPropagation()}>
        <div style={S.accent} />

        <div style={S.head}>
          <div style={S.titleWrap}>
            <img src="/static/images/iglu_logo_transparent.png" alt="" style={S.logo} />
            <h4 style={S.title}>Departure Clean Upload</h4>
          </div>
          <button type="button" style={{ ...S.closeX, ...(busy ? { opacity: 0.35, cursor: "not-allowed" } : {}) }}
            disabled={busy} onClick={busy ? undefined : close} aria-label="Close">×</button>
        </div>

        {/* dropzone */}
        {!result && (
          <div
            style={S.drop(dragOver)}
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); pickFile(e.dataTransfer.files?.[0]); }}
          >
            <input ref={inputRef} type="file" accept=".xlsx,.xlsm" style={{ display:"none" }}
              onChange={(e) => { pickFile(e.target.files?.[0]); e.target.value = ""; }} />
            <div style={S.dropTitle}>
              {busy && !preview ? "Reading file…" : file ? (
                <>
                  <span>📄 {file.name}</span>
                  <button type="button" style={{ ...S.removeX, ...(busy ? { opacity: 0.35, cursor: "not-allowed" } : {}) }} disabled={busy}
                    title="Remove file" aria-label="Remove file"
                    onClick={(e) => { e.stopPropagation(); if (!busy) reset(); }}>×</button>
                </>
              ) : "Drag & drop the requests.xlsx template here"}
            </div>
            <div style={S.dropHint}>{file ? "Drop a different file to replace it" : "or click to browse"}</div>
          </div>
        )}

        <div style={S.templateRow}>
          <span style={S.hint}>All columns are required · blank Comments save as "No Comments"</span>
          <a href={TEMPLATE_URL} download="requests.xlsx" style={S.templateBtn}
            onClick={(e) => e.stopPropagation()}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
            </svg>
            Download Template
          </a>
        </div>

        {error && <div style={S.err}>{error}</div>}

        {/* totals + tables */}
        {preview && !result && (
          <>
            <div style={S.buttonsGrid}>
              {BUCKETS.map((b) => (
                <button key={b.key} type="button" style={S.countBtn(selection === b.key)}
                  onClick={() => setSelection((p) => (p === b.key ? null : b.key))}>
                  <span style={S.countLabel}>{b.label}</span>
                  <span style={S.countValue(b.tone)}>{busy ? "…" : counts[b.key]}</span>
                </button>
              ))}
            </div>

            {selection === "total"   && table(rows, true)}
            {selection === "valid"   && table(validRows, false)}
            {selection === "invalid" && table(invalidRows, true)}

            <label style={S.emailRow}>
              <input type="checkbox" checked={emailReport} onChange={(e) => setEmailReport(e.target.checked)}
                style={{ accentColor: ACCENT }} />
              Email PDF of the pushed rows to the selected recipients
            </label>

            {phase && (
              <div style={{ margin: "10px 0 4px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.75rem", color: "rgba(255,255,255,0.6)", marginBottom: 6 }}>
                  <span>{stage || "Ready"}</span>
                  <span>{shown} / {counts.valid} · {Math.round(visual)}%</span>
                </div>
                <div style={{ width: "100%", height: 8, borderRadius: 999, background: "rgba(255,255,255,0.08)", overflow: "hidden" }}>
                  <div style={{ width: `${visual}%`, height: "100%", borderRadius: 999, transition: "width .15s linear",
                    background: "linear-gradient(90deg,#16a34a,#4ade80)" }} />
                </div>
              </div>
            )}
            <button type="button" style={{ ...S.submit(busy, counts.valid > 0), ...(busy ? { pointerEvents: "none" } : {}) }}
              onClick={busy ? undefined : commit} disabled={busy || counts.valid === 0}>
              {busy ? <Spinner /> : "⬆"} {busy ? (stage || "Working…") : `Push ${counts.valid} of ${counts.total} row${counts.total === 1 ? "" : "s"} to South Yarra`}
              {!busy && counts.invalid > 0 ? ` (${counts.invalid} with errors skipped)` : ""}
            </button>
            <div style={S.hint}>
              Nothing is written until you push. Rows with errors are skipped — fix them in the spreadsheet and drop it again.
            </div>
          </>
        )}

        {result && (
          <div style={S.done}>
            ✅ Pushed — {result.created} created
            {result.skipped ? `, ${result.skipped} skipped` : ""}
            {result.failed ? `, ${result.failed} failed` : ""} of {result.total} row{result.total === 1 ? "" : "s"}
            {result.emailed && <div style={{ ...S.doneMsg, color:"#4ade80", textAlign:"center" }}>📧 PDF request emailed</div>}
            {result.emailError && <div style={S.doneMsg}>• Email failed: {result.emailError}</div>}
            {result.messages?.length > 0 && (
              <div style={S.doneMsg}>{result.messages.map((m, i) => <div key={i}>• {m}</div>)}</div>
            )}
            <div style={{ marginTop:10 }}>
              <button type="button" style={S.smallBtn} onClick={reset}>Upload another file</button>
            </div>
          </div>
        )}

        <button type="button" style={{ ...S.closeBtn, ...(busy ? { opacity: 0.45, cursor: "not-allowed", pointerEvents: "none" } : {}) }}
          onClick={busy ? undefined : close} disabled={busy}>Close</button>
      </div>
    </div>
  );
};

export default DepartureUpload;